/**
 * @description Hook for CMS feature entitlement (tab visibility) — LYN-21
 *
 * @dependencies
 * - cmsService: GET /api/features/cms-access
 * - CompanyContext: skip the fetch until a company exists
 *
 * @notes
 * Fail closed: no company and fetch errors both yield allowed=false.
 * MerchantSidebar ANDs this with cms.view — it must never treat error
 * as a reason to show the Website tab.
 */

import { useState, useEffect } from 'react';
import cmsService from '../services/api/cmsService';
import { useCompany } from '../contexts/CompanyContext';

/**
 * @returns {{ allowed: boolean, loading: boolean, error: string|null }}
 */
export default function useCmsAccess() {
  const { hasCompany } = useCompany();
  const [allowed, setAllowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!hasCompany) {
      setAllowed(false);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const access = await cmsService.getCmsAccess();
        if (!cancelled) {
          setAllowed(!!access.allowed);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err.response?.data?.error?.message
            || err.message
            || 'Failed to load CMS access';
          setError(message);
          setAllowed(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [hasCompany]);

  return { allowed, loading, error };
}
